<template>
  <div class="p-site-keyword-analysis-form p-site-keyword-analysis-form--task">
    <div class="p-site-keyword-analysis-form__header">
      <Icon variant="project_tasklist" :size="18" radius />
      <h3 class="p-site-keyword-analysis-form__title">
        選択中のキーワードにタスクを登録する
      </h3>
    </div>
    <div class="p-site-keyword-analysis-form__content">
      <div class="p-site-keyword-analysis-form__col">
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">施策内容を選択する</p>
          </div>
          <div class="c-fieldset__content">
            <Select
              v-model="params.type"
              :options="types"
              placeholder="施策内容を選択してください"
              :selected="params.type"
              size="sm"
              required
            />
          </div>
        </div>
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">タスクに担当者を設定する</p>
          </div>
          <div class="c-fieldset__content c-alertPos">
            <Select
              v-model="params.operator"
              :options="projectUsers"
              placeholder="担当者なし"
              :selected="params.operator"
              size="sm"
              :class="{
                alert: error.operator
              }"
            />
            <Alert v-if="error.operator">
              {{ error.operator }}
            </Alert>
          </div>
        </div>
      </div>
      <div class="p-site-keyword-analysis-form__col">
        <div class="c-fieldset c-fieldset--wrap">
          <div class="c-fieldset__header">
            <p class="c-fieldset__title">詳細</p>
          </div>
          <div class="c-fieldset__content c-alertPos">
            <Textarea
              v-model="params.text"
              placeholder="タスクの詳細を記入してください"
              size="sm"
              :class="{
                alert: isEdited.text && error.text
              }"
            />
            <Alert v-if="isEdited.text && error.text">
              {{ error.text }}
            </Alert>
          </div>
        </div>
        <div class="c-alertPos l-mt-3">
          <Button
            variant="main"
            fsize="f14"
            icon="plus"
            block
            class="l-pr-2"
            :class="{ 'is-loading': isProcessing }"
            :disabled="isDisabled"
            @click="submit"
          >
            タスクを追加
          </Button>
          <Alert v-if="errorResponse">
            {{ errorResponse }}
          </Alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Select from '@/components/Select';
import Textarea from '@/components/Textarea';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    count: {
      type: Number,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    conversionType: {
      type: String,
      required: true
    },
    dataType: {
      type: String,
      required: true
    }
  },
  components: {
    Alert,
    Button,
    Icon,
    Select,
    Textarea
  },
  data() {
    return {
      error: {
        operator: '',
        text: '',
        type: ''
      },
      errorResponse: '',
      params: {
        operator: '',
        text: '',
        type: ''
      },
      usages: {
        rest: 0
      },
      isEdited: {
        operator: false,
        text: false,
        type: false
      },
      isError: false,
      isOver: false,
      isProcessing: false
    };
  },
  computed: {
    projectUsers() {
      return this.$store.getters['project/usersSelect'];
    },
    types() {
      return this.$store.getters['task/solutionSelect'];
    },
    isDisabled() {
      return !this.count || this.isError || this.isProcessing;
    }
  },
  watch: {
    count: {
      handler() {
        this.resetError();
      }
    },
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        this.resetError();
      },
      deep: true
    },
    'params.operator': {
      handler(value) {
        const operator = value;
        this.error.operator = '';
        this.isEdited.operator = operator ? true : false;
      },
      immediate: true
    },
    'params.text': {
      handler() {
        let text = this.params.text.trim();
        this.error.text = new Validation()
          .input()
          .maxLength(TASK_LENGTHS_LIMIT)
          .test(text);
        this.isEdited.text = text ? true : false;
      },
      immediate: true
    },
    'params.type': {
      handler() {
        let type = this.params.type;
        this.error.type = new Validation().select().test(type);
        this.isEdited.type = type ? true : false;
      },
      immediate: true
    }
  },
  created() {
    this.$store.dispatch('project/getUsers');
    this.$store.dispatch('task/getTaskSolutions');
  },
  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    async submit() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      // 利用量チェック
      await this.getUsages();
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }
      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await Promise.all(
        this.items.map(item => {
          return this.postTask(item);
        })
      )
        .then(response => {
          this.params.operator = '';
          this.params.text = '';
          this.params.type = '';
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });

      this.$store.dispatch('project/getTaskCount');
      this.isProcessing = false;

      this.$emit('reset');
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const usagesRest = usages.max - usages.current;
          const error = utils.getErrorUsages({
            count: this.count,
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse = error.message;
          }
          if (usagesRest > 0 && usagesRest < this.count) {
            this.usages.rest = usagesRest;
            this.isOver = true;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        const existUser = this.projectUsers.some(
          user => user.value === this.params.operator
        );
        if (!existUser) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask(item) {
      const params = {
        column: 'todo',
        keyword: null,
        operatorUserId: this.params.operator || null,
        taskDetail: this.params.text,
        type: this.params.type,
        url: null
      };

      params.keyword = item.keyword;
      params.url = item.url;

      // タスクの詳細欄にキーワード情報を追加
      this.setTaskDetail(params, item);

      return apiProjects.tasks.siteAnalysis.post(
        params,
        this.auth.idToken,
        this.project.id
      );
    },
    setTaskDetail(params, item) {
      let detail = params.taskDetail;
      // タイトル変更・リライト
      if (this.params.type === 'changeTd' || this.params.type === 'rewrite') {
        if (item.rank != null)
          detail += '\n【平均順位】 ' + Math.round(item.rank * 10) / 10 + '位';
        // 流入数による分析
        if (this.dataType === 'influx') {
          if (item.searchVolume != null)
            detail +=
              '\n【月間検索数】 ' + this.separateNumber(item.searchVolume);
        }
        if (item.impressions != null)
          detail += '\n【表示回数】 ' + this.separateNumber(item.impressions);
        if (item.ctr != null)
          detail +=
            '\n【クリック率】 ' + Math.round(item.ctr * 100) / 100 + '%';
        if (item.influx != null)
          detail += '\n【流入数】 ' + this.separateNumber(item.influx);
        // 貢献キーワード(直接)
        if (
          this.dataType === 'conversion' &&
          this.conversionType === 'direct'
        ) {
          if (item.directConversion != null)
            detail +=
              '\n【直接CV】 ' + this.separateNumber(item.directConversion);
          if (item.directConversionRate != null)
            detail +=
              '\n【直接CVR】 ' +
              Math.round(this.separateNumber(item.directConversionRate) * 100) /
                100 +
              '%';
        }
        // 貢献キーワード(間接)
        if (
          this.dataType === 'conversion' &&
          this.conversionType === 'assisted'
        ) {
          if (item.directConversion != null)
            detail +=
              '\n【間接CV】 ' + this.separateNumber(item.assistedConversion);
          if (item.directConversionRate != null)
            detail +=
              '\n【間接CVR】 ' +
              Math.round(
                this.separateNumber(item.assistedConversionRate) * 100
              ) /
                100 +
              '%';
        }
        params.taskDetail = detail;
        return;
      }
      // 新規コンテンツ作成
      if (this.params.type === 'createContent') {
        // 流入数による分析
        if (this.dataType === 'influx') {
          if (item.rank != null)
            detail +=
              '\n【平均順位】 ' + Math.round(item.rank * 10) / 10 + '位';
          if (item.searchVolume != null)
            detail +=
              '\n【月間検索数】 ' + this.separateNumber(item.searchVolume);
        }
        // 貢献キーワード(直接・間接)
        if (this.dataType === 'conversion') {
          if (item.rank != null)
            detail +=
              '\n【平均順位】 ' + Math.round(item.rank * 10) / 10 + '位';
        }
        params.taskDetail = detail;
        return;
      }
    },
    separateNumber(number) {
      if (number === null) {
        return number;
      }
      return String(number).replace(/^(-?\d+)(?=\.|$)/, s => {
        return s.replace(/(\d+?)(?=(?:\d{3})+$)/g, '$1,');
      });
    },
    resetError() {
      if (!this.isOver || this.usages.rest >= this.count) {
        this.errorResponse = '';
        this.isOver = false;
      }
    }
  }
};
</script>
